import { Option } from '@/types';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { useEffect, useRef, useState } from 'react';

export interface CheckAndCountFieldProps {
  id?: string;
  label?: string;
  name: string;
  options: Option[];
  error?: string;
  helpers?: FieldHelperProps<any>;
  field?: FieldInputProps<any>;
  meta?: FieldMetaProps<any>;
  isDisabled?: boolean;
  labelFontSize?: string;
  labelMb?: string;
  isRequired?: boolean;
  [x: string]: any;
}

export function CheckAndCountField(props: CheckAndCountFieldProps) {
  const {
    id,
    label,
    name,
    options,
    error,
    helpers,
    field,
    meta,
    isDisabled,
    labelFontSize,
    labelMb,
    isRequired,
    onDark,
  } = props;

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [itemCounts, setItemCounts] = useState<{ [key: string]: string }>({});
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    if (field?.value && Array.isArray(field.value)) {
      const newSelectedItems = new Set<string>();
      const newItemCounts = { ...itemCounts };

      field.value.forEach((item: { id: string; count: number }) => {
        newSelectedItems.add(item.id);
        newItemCounts[item.id] = item.count?.toString() || '1';
      });

      setSelectedItems(newSelectedItems);
      setItemCounts(newItemCounts);
    }
  }, [field?.value]);

  const handleItemToggle = (itemValue: string) => {
    if (isDisabled) return;

    const newSelectedItems = new Set(selectedItems);
    const newItemCounts = { ...itemCounts };

    if (newSelectedItems.has(itemValue)) {
      newSelectedItems.delete(itemValue);
      delete newItemCounts[itemValue];
    } else {
      newSelectedItems.add(itemValue);
      newItemCounts[itemValue] = '1';

      setTimeout(() => {
        const inputRef = inputRefs.current[itemValue];
        if (inputRef) {
          inputRef.focus();
          inputRef.select();
        }
      }, 0);
    }

    setSelectedItems(newSelectedItems);
    setItemCounts(newItemCounts);

    const formikValue = Array.from(newSelectedItems).map((id) => ({
      id,
      count: parseInt(newItemCounts[id]) || 1,
    }));

    if (helpers) {
      helpers.setValue(formikValue);
    }
  };

  const handleInputChange = (itemValue: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    const value = e.target.value;

    setItemCounts({
      ...itemCounts,
      [itemValue]: value,
    });

    const numValue = value === '' ? 0 : parseInt(value) || 0;

    const formikValue = Array.from(selectedItems).map((id) => ({
      id,
      count: id === itemValue ? numValue : parseInt(itemCounts[id]) || 1,
    }));

    if (helpers) {
      helpers.setValue(formikValue);
    }
  };

  const handleInputBlur = (itemValue: string, e: React.FocusEvent<HTMLInputElement>) => {
    if (itemCounts[itemValue] === '') {
      e.preventDefault();
      inputRefs.current[itemValue]?.focus();
      return;
    }

    const currentValue = itemCounts[itemValue];

    if (currentValue === '' || isNaN(parseInt(currentValue))) {
      setItemCounts({
        ...itemCounts,
        [itemValue]: '1',
      });
    }

    const formikValue = Array.from(selectedItems).map((id) => ({
      id,
      count:
        id === itemValue
          ? currentValue === '' || isNaN(parseInt(currentValue))
            ? 1
            : parseInt(currentValue)
          : parseInt(itemCounts[id]) || 1,
    }));

    if (helpers) {
      helpers.setValue(formikValue);
    }
  };

  const handleKeyDown = (itemValue: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && itemCounts[itemValue].length === 1) {
      setTimeout(() => {
        if (
          inputRefs.current[itemValue] &&
          document.activeElement !== inputRefs.current[itemValue]
        ) {
          inputRefs.current[itemValue]?.focus();
        }
      }, 0);
    }
  };

  const setInputRef = (itemValue: string, el: HTMLInputElement | null) => {
    inputRefs.current[itemValue] = el;
  };

  const formikError = error || (meta && meta.touched && meta.error);

  const isError = !!formikError;
  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  return (
    <FormControl isRequired={isRequired} isInvalid={isError} isDisabled={isDisabled}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize={labelFontSize || '2xl'}
          lineHeight="normal"
          color={textColorPrimary}
          fontWeight="bold"
          mb={labelMb || 4}
          _hover={{ cursor: 'pointer' }}
        >
          {label}
        </FormLabel>
      )}

      <Flex direction="column" gap={4}>
        {options.map((option) => {
          const isSelected = selectedItems.has(option.value);

          return (
            <HStack key={option.value} height="38px">
              <Flex
                as={Button}
                w="55px"
                minW="55px"
                h="24px"
                backgroundColor={isSelected ? 'primary.500' : 'gray.600'}
                color={isSelected ? 'primaryText' : 'initial'}
                borderRadius="4px"
                justifyContent="center"
                alignItems="center"
                onClick={() => handleItemToggle(option.value)}
              >
                <Text fontSize="xs" fontWeight="bold">
                  {option.value}
                </Text>
              </Flex>
              <Text overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" maxW="70%">
                {option.label}
              </Text>
              {isSelected && (
                <Box
                  ml="auto"
                  pl={3}
                  onClick={(e) => {
                    e.stopPropagation();
                    inputRefs.current[option.value]?.focus();
                  }}
                >
                  <Input
                    ref={(el) => setInputRef(option.value, el)}
                    value={itemCounts[option.value] || ''}
                    onChange={(e) => handleInputChange(option.value, e)}
                    onBlur={(e) => handleInputBlur(option.value, e)}
                    onKeyDown={(e) => handleKeyDown(option.value, e)}
                    isDisabled={isDisabled}
                    size="sm"
                    borderRadius="md"
                    textAlign="center"
                    width="60px"
                    bg="white"
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                  />
                </Box>
              )}
            </HStack>
          );
        })}
      </Flex>

      {isError && (
        <FormErrorMessage>
          {typeof formikError === 'string' ? formikError : 'Invalid selection'}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}
